/**
 * /p/:slug — public profile view. Anonymous rate flow with fingerprint
 * dedupe + invisible hCaptcha.
 *
 * Layout mirrors the desktop design (public-screens.jsx →
 * PublicProfileScreen):
 *   - Left column (1.1fr): 4:5 photo carousel with chevron nav + name
 *     overlay + thin progress bars across the top. Below: About + Music.
 *   - Right column (0.9fr): sticky RatingPanel (pre-vote) or
 *     AggregatePanel (post-vote) at top: 92px. Privacy chip below.
 *
 * Renders inside PublicShell — top nav + footer are inherited.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {
  APIError, HCAPTCHA_SITE_KEY, type PublicProfile, type User,
  RATING_NOTE_MAX, api, dmApi, profilesApi, publicApi, sessionStore,
} from '@/lib/api';
import { MusicSplit } from '@/components/MusicCards';
import { getOrCreateFingerprint } from '@/lib/fingerprint';
import { Btn, Chip, Eyebrow, Icons, RingChart } from '@/components';
import { ReportProfileModal } from '@/components/ReportProfileModal';
import { PaywallSheet } from '@/components/PaywallSheet';
import { friendlyError } from '@/lib/errors';

const localVerdictKey = (slug: string) => `wydm.rated:${slug}`;

export function PublicProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  const [myVerdict, setMyVerdict] = useState<'yes' | 'no' | null>(() => {
    if (!slug) return null;
    try {
      const v = localStorage.getItem(localVerdictKey(slug));
      return v === 'yes' || v === 'no' ? v : null;
    } catch { return null; }
  });
  const [submitting, setSubmitting] = useState<'yes' | 'no' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const captchaRef = useRef<HCaptcha | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  const pendingVerdictRef = useRef<'yes' | 'no' | null>(null);

  const signedIn = useMemo(() => !!sessionStore.getAccess(), []);

  // Owner-view detection — when the signed-in user is looking at their
  // OWN /p/:slug we suppress the rating panel + privacy chip so they
  // just see their own card. profilesApi.me() is the cheapest way to
  // get the viewer's slug; runs in parallel with the profile fetch
  // and stays null for anonymous visitors.
  const [isOwner, setIsOwner] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  // Viewer's tier — needed by the DM-this-user button to know whether
  // to start the DM flow or open the upgrade modal.
  const [viewer, setViewer] = useState<User | null>(null);
  useEffect(() => {
    if (!signedIn || !slug) return;
    let cancelled = false;
    profilesApi.me()
      .then((p) => { if (!cancelled) setIsOwner(p.slug === slug); })
      .catch(() => null);
    api.me()
      .then((u) => { if (!cancelled) setViewer(u); })
      .catch(() => null);
    return () => { cancelled = true; };
  }, [signedIn, slug]);

  useEffect(() => {
    if (!slug) { setLoadError('Missing slug.'); return; }
    publicApi.getProfile(slug)
      .then(setProfile)
      .catch((err) => {
        if (err instanceof APIError && err.status === 404) {
          setLoadError('This profile is not available.');
        } else {
          setLoadError(friendlyError(err, 'PublicProfile'));
        }
      });
  }, [slug]);

  // OG / Twitter meta on render (helps human visitors using their browser
  // share sheet from this page; crawlers still get index.html defaults).
  useEffect(() => {
    if (!profile) return;
    const title = `${profile.display_name}${profile.age ? `, ${profile.age}` : ''} · Would You Date Me?`;
    const desc = profile.bio ? truncate(profile.bio, 200) : `Rate ${profile.display_name}'s profile — Yes or No, anonymously.`;
    const image = profile.photos[0]?.url ?? '';
    const url = window.location.href;
    document.title = title;
    setMeta('description', desc);
    setMetaProperty('og:title', title);
    setMetaProperty('og:description', desc);
    setMetaProperty('og:type', 'profile');
    setMetaProperty('og:url', url);
    if (image) setMetaProperty('og:image', image);
    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    if (image) setMeta('twitter:image', image);
    return () => { document.title = 'Would You Date Me?'; };
  }, [profile]);

  const doSubmit = async (verdict: 'yes' | 'no', captchaToken: string | null) => {
    if (!slug) return;
    setSubmitError(null);
    try {
      const fp = getOrCreateFingerprint();
      const trimmedNote = note.trim();
      const resp = await publicApi.rate(slug, {
        verdict,
        fingerprint: fp,
        ...(trimmedNote ? { note: trimmedNote } : {}),
        ...(captchaToken ? { captcha_token: captchaToken } : {}),
      });
      setMyVerdict(resp.your_verdict);
      setProfile((p) => p ? { ...p, rating_count: resp.rating_count, yes_percent: resp.yes_percent } : p);
      try { localStorage.setItem(localVerdictKey(slug), verdict); } catch { /* noop */ }
    } catch (err) {
      if (err instanceof APIError) {
        if (err.code === 'RATE_LIMIT') {
          setSubmitError('Too many ratings from your network — try again later.');
        } else if (err.code === 'CAPTCHA_REQUIRED' || err.code === 'CAPTCHA_FAILED') {
          setSubmitError('Please complete the captcha and try again.');
          captchaTokenRef.current = null;
          captchaRef.current?.resetCaptcha();
        } else if (err.code === 'COMMENT_REJECTED') {
          setSubmitError("Your note didn't pass our content check. Rewrite it or remove it, then try again.");
        } else {
          setSubmitError(err.message);
        }
      } else {
        setSubmitError('Could not submit rating.');
      }
    } finally {
      setSubmitting(null);
      pendingVerdictRef.current = null;
    }
  };

  const submitRating = async (verdict: 'yes' | 'no') => {
    if (!slug || submitting) return;
    setSubmitting(verdict);
    setSubmitError(null);
    if (!HCAPTCHA_SITE_KEY || !captchaRef.current) {
      await doSubmit(verdict, null);
      return;
    }
    pendingVerdictRef.current = verdict;
    captchaRef.current.execute();
  };

  if (loadError) {
    return (
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '120px 24px', textAlign: 'center' }}>
        <h1 className="serif" style={{ fontSize: 36, fontStyle: 'italic', fontWeight: 500, marginBottom: 12 }}>
          Not found
        </h1>
        <p style={{ color: 'var(--ink-3)', marginBottom: 24 }}>{loadError}</p>
        <Link to="/" style={{ textDecoration: 'none' }}><Btn variant="primary">Back to home</Btn></Link>
      </main>
    );
  }

  if (!profile) {
    return (
      <main style={{ textAlign: 'center', padding: '120px 20px', color: 'var(--ink-3)' }}>Loading…</main>
    );
  }

  const photo = profile.photos[photoIdx];
  const hasPhotos = profile.photos.length > 0;
  const firstName = profile.display_name.split(' ')[0];

  return (
    <main
      className="pub-shell"
      style={{
        // Narrower wrapper for the owner-only view so the single-column
        // profile is centred + has a comfortable reading width. Rater
        // view stays at 1280 to give the sticky right rating panel room.
        maxWidth: isOwner ? 720 : 1280,
        margin: '0 auto',
        padding: '32px 40px 60px',
      }}
    >
      <div
        className="pub-grid"
        style={{
          display: 'grid',
          // Owners skip the rating panel entirely; the right column
          // collapses + the left fills the wrapper.
          gridTemplateColumns: isOwner ? '1fr' : '1.1fr 0.9fr',
          gap: 40,
          alignItems: 'flex-start',
        }}
      >
        {/* LEFT — Photos + About + Music */}
        <div>
          {hasPhotos ? (
            <div
              style={{
                position: 'relative',
                aspectRatio: '4/5',
                borderRadius: 28,
                overflow: 'hidden',
                boxShadow: 'var(--shadow-card)',
                background: 'var(--surface-2)',
              }}
            >
              <img
                src={photo.url}
                alt={`${profile.display_name} photo ${photoIdx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                draggable={false}
              />
              {/* Progress bars across the top — one per photo */}
              {profile.photos.length > 1 && (
                <div style={{ position: 'absolute', top: 18, left: 18, right: 18, display: 'flex', gap: 5 }}>
                  {profile.photos.map((_, i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 3,
                        background: i <= photoIdx ? '#fff' : 'rgba(255,255,255,0.3)',
                        transition: 'background 0.2s',
                      }}
                    />
                  ))}
                </div>
              )}
              {/* Prev / next chevrons */}
              {profile.photos.length > 1 && (
                <>
                  <CarouselBtn side="left" disabled={photoIdx === 0} onClick={() => setPhotoIdx((i) => Math.max(0, i - 1))} />
                  <CarouselBtn side="right" disabled={photoIdx === profile.photos.length - 1} onClick={() => setPhotoIdx((i) => Math.min(profile.photos.length - 1, i + 1))} />
                </>
              )}
              {/* Name + age overlay — chips for gender + looking-for
                  moved out of the overlay into a dedicated row below
                  the photo (more legible on mobile + above-the-fold). */}
              <div style={{ position: 'absolute', left: 28, bottom: 28, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                <div className="serif pub-name" style={{ fontSize: 56, lineHeight: 1, fontWeight: 500, letterSpacing: -1 }}>
                  {profile.display_name}
                  {profile.age && <span style={{ fontStyle: 'italic', fontWeight: 400 }}>, {profile.age}</span>}
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                aspectRatio: '4/5',
                borderRadius: 28,
                background: 'var(--surface-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink-3)',
              }}
            >
              No photos
            </div>
          )}

          {/* Identity chips — gender + looking-for. Sits right under
              the photo carousel so it's visible above-the-fold on
              mobile (one of the most important signals for raters). */}
          {(profile.gender || profile.looking_for.length > 0) && (
            <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {profile.gender && <Chip>{capitalize(profile.gender)}</Chip>}
              {profile.looking_for.length > 0 && (
                <Chip>
                  Looking for {profile.looking_for.map((lf) => capitalize(lf)).join(' · ').toLowerCase()}
                </Chip>
              )}
            </div>
          )}

          {/* DM-this-user CTA. Hidden for owners (they can't DM
              themselves anyway) — every other visitor sees the button.
              The click handler picks a modal based on viewer state:
                no session → Sign up to DM
                free tier → Get premium to DM
                premium   → create thread + navigate to /dm/[id]
              Backend still enforces all of this (NOT_RATED, PREMIUM_REQUIRED,
              SELF_DM) — these gates are purely UX. */}
          {!isOwner && (
            <div style={{ marginTop: 16 }}>
              <DMUserButton
                slug={profile.slug}
                displayName={profile.display_name}
                signedIn={signedIn}
                isPremium={viewer?.tier === 'premium'}
              />
            </div>
          )}

          {/* About */}
          {profile.bio && (
            <div style={{ marginTop: 32 }}>
              <Eyebrow letter={2} style={{ marginBottom: 12 }}>About</Eyebrow>
              <div className="serif pub-bio" style={{ fontSize: 24, lineHeight: 1.4, letterSpacing: -0.2 }}>
                {profile.bio}
              </div>
            </div>
          )}

          {/* Music — MusicSplit renders its own 'On repeat' / 'Top
              artists' eyebrows per column, so the wrapper section
              eyebrow was redundant. */}
          {((profile.music?.length ?? 0) > 0 || (profile.music_artists?.length ?? 0) > 0) && (
            <div style={{ marginTop: 32 }}>
              <MusicSplit
                tracks={profile.music ?? []}
                artists={profile.music_artists ?? []}
                tracksLabel="On repeat"
                artistsLabel="Top artists"
              />
            </div>
          )}

        </div>

        {/* RIGHT — sticky rating / aggregate panel + privacy chip.
            Owners viewing their own /p/:slug don't see this column at
            all — they get the profile centred in a narrower wrapper
            and rely on the dashboard's "Go to profile" pill to navigate
            back. Backend still trusts fingerprint dedupe on the rate
            write path; this is purely a UI gate. */}
        {!isOwner && (
        <div className="pub-right" style={{ position: 'sticky', top: 92 }}>
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: 28,
              padding: 28,
              boxShadow: 'var(--shadow-lift)',
              animation: myVerdict ? 'rise 0.5s ease' : undefined,
            }}
          >
            {myVerdict ? (
              <RatingReveal
                myVerdict={myVerdict}
                count={profile.rating_count ?? null}
                yesPercent={profile.yes_percent ?? null}
                onRevote={() => setMyVerdict(null)}
                onMakeOwn={() => navigate(signedIn ? '/builder' : '/auth/signup')}
                signedIn={signedIn}
              />
            ) : (
              <>
                <div
                  className="serif"
                  style={{
                    fontSize: 30,
                    lineHeight: 1.1,
                    fontWeight: 500,
                    letterSpacing: -0.5,
                    textAlign: 'center',
                  }}
                >
                  Would you date <span style={{ fontStyle: 'italic' }}>{firstName}</span>?
                </div>
                <div
                  className="mono"
                  style={{
                    textAlign: 'center',
                    fontSize: 12,
                    color: 'var(--ink-3)',
                    marginTop: 8,
                    marginBottom: 24,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  Anonymous · No signup needed
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                  <VerdictButton
                    verdict="no"
                    selected={false}
                    busy={submitting === 'no'}
                    disabled={submitting !== null}
                    onClick={() => submitRating('no')}
                  />
                  <VerdictButton
                    verdict="yes"
                    selected={false}
                    busy={submitting === 'yes'}
                    disabled={submitting !== null}
                    onClick={() => submitRating('yes')}
                  />
                </div>

                <NoteField value={note} onChange={setNote} disabled={submitting !== null} />

                {HCAPTCHA_SITE_KEY && (
                  <div style={{ marginTop: 8 }}>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      size="invisible"
                      onVerify={(token) => {
                        captchaTokenRef.current = token;
                        const v = pendingVerdictRef.current;
                        if (v) void doSubmit(v, token);
                      }}
                      onError={(err) => {
                        captchaTokenRef.current = null;
                        setSubmitError(`Captcha error: ${err}`);
                        setSubmitting(null);
                        pendingVerdictRef.current = null;
                      }}
                      onExpire={() => { captchaTokenRef.current = null; }}
                    />
                  </div>
                )}

                {submitError && (
                  <p style={{ marginTop: 12, fontSize: 12.5, color: '#a8331f', textAlign: 'center' }}>
                    {submitError}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Privacy chip */}
          <div
            style={{
              marginTop: 16,
              padding: 14,
              background: 'var(--surface-2)',
              borderRadius: 14,
              fontSize: 12,
              color: 'var(--ink-3)',
              lineHeight: 1.5,
              display: 'flex',
              gap: 10,
            }}
          >
            <Icons.lock size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              You're anonymous. {profile.display_name} sees the rating and the note, never your name. No signup needed.
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Report this profile — small footer affordance. Hidden for the
          owner of the profile (they can't usefully report themselves)
          and rendered as a low-key text button rather than a primary
          CTA so it doesn't compete with the rate-or-DM moment. */}
      {!isOwner && (
        <div className="pub-report-row" style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--ink-3)',
              fontSize: 13,
              padding: '6px 12px',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            aria-label={`Report ${profile.display_name}'s profile`}
          >
            <Icons.flag size={13} />
            Report this profile
          </button>
        </div>
      )}

      <ReportProfileModal
        open={reportOpen}
        slug={profile.slug}
        onClose={() => setReportOpen(false)}
      />
    </main>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function CarouselBtn({ side, disabled, onClick }: { side: 'left' | 'right'; disabled: boolean; onClick: () => void }) {
  const ChevIcon = side === 'left' ? Icons.chevL : Icons.chevR;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous photo' : 'Next photo'}
      style={{
        position: 'absolute',
        [side]: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 44,
        height: 44,
        borderRadius: 44,
        background: 'rgba(255,255,255,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        border: 'none',
        color: 'var(--ink)',
      }}
    >
      <ChevIcon size={18} />
    </button>
  );
}

// DMUserButton — sits below the identity chips. Click handler picks
// the right path based on viewer state:
//   not signed in  → open SignUpToMessage modal (CTA to /auth/signup)
//   free tier      → open PaywallSheet (DM trigger)
//   premium tier   → POST /dm/threads with the recipient slug, then
//                    navigate to /dm/[id]. The backend enforces
//                    NOT_RATED + BLOCKED + SELF_DM; we surface the
//                    error inline.
function DMUserButton({
  slug, displayName, signedIn, isPremium,
}: { slug: string; displayName: string; signedIn: boolean; isPremium: boolean }) {
  const navigate = useNavigate();
  const [modal, setModal] = useState<null | 'signup' | 'upgrade'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    setErr(null);
    if (!signedIn) { setModal('signup'); return; }
    if (!isPremium) { setModal('upgrade'); return; }
    setBusy(true);
    try {
      const thread = await dmApi.createThread({ recipient_slug: slug });
      navigate(`/dm/${thread.id}`);
    } catch (e) {
      if (e instanceof APIError) {
        if (e.code === 'PREMIUM_REQUIRED') setModal('upgrade');
        else setErr(e.message);
      } else {
        setErr('Could not start a DM.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          borderRadius: 100,
          background: 'var(--ink)',
          color: 'var(--bg)',
          border: 'none',
          fontSize: 13,
          fontWeight: 600,
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
          fontFamily: 'var(--font-ui)',
        }}
      >
        <Icons.chat size={14} stroke={2.2} />
        {busy ? 'Opening…' : `Send ${displayName.split(' ')[0]} a DM`}
      </button>
      {err && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#a8331f' }}>{err}</div>
      )}
      {modal === 'signup' && (
        <DMGateModal
          eyebrow="Sign up to DM"
          title={<>Make an account to <span style={{ fontStyle: 'italic' }}>say hi.</span></>}
          body={<>You need an account to DM {displayName}. It only takes a minute, and you can rate other profiles too.</>}
          ctaLabel="Create my account"
          ctaTo="/auth/signup"
          onClose={() => setModal(null)}
        />
      )}
      <PaywallSheet
        open={modal === 'upgrade'}
        trigger="dm_send"
        onClose={() => setModal(null)}
      />
    </>
  );
}

function DMGateModal({
  eyebrow, title, body, ctaLabel, ctaTo, onClose,
}: {
  eyebrow: string;
  title: React.ReactNode;
  body: React.ReactNode;
  ctaLabel: string;
  ctaTo: string;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 20, animation: 'fade-in 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 22, padding: 28,
          width: '100%', maxWidth: 400, boxShadow: 'var(--shadow-lift)',
          animation: 'scale-in 0.2s ease',
        }}
      >
        <Eyebrow tone="coral" style={{ marginBottom: 8 }}>{eyebrow}</Eyebrow>
        <h2 className="serif" style={{ fontSize: 26, fontWeight: 500, fontStyle: 'italic', margin: 0, marginBottom: 8, lineHeight: 1.15 }}>
          {title}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5, margin: 0, marginBottom: 20 }}>
          {body}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" size="md" onClick={onClose}>Not now</Btn>
          <Link to={ctaTo} style={{ textDecoration: 'none' }}>
            <Btn variant="primary" size="md">{ctaLabel}</Btn>
          </Link>
        </div>
      </div>
    </div>
  );
}

function VerdictButton({
  verdict, selected, busy, disabled, onClick,
}: {
  verdict: 'yes' | 'no';
  selected: boolean;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const isYes = verdict === 'yes';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1,
        height: 72,
        borderRadius: 20,
        background: selected || busy
          ? (isYes ? 'var(--coral)' : 'var(--ink)')
          : 'var(--chip)',
        color: selected || busy
          ? (isYes ? '#fff' : 'var(--bg)')
          : 'var(--ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        fontSize: 19,
        fontWeight: 500,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s, color 0.2s',
        animation: busy && isYes ? 'pulse-coral 1.2s ease-out' : undefined,
        opacity: disabled && !busy ? 0.5 : 1,
      }}
    >
      {isYes
        ? <Icons.heart size={22} fill={selected || busy ? 'currentColor' : 'none'} />
        : <Icons.x size={22} stroke={2.4} />}
      {isYes ? 'Yes' : 'No'}
    </button>
  );
}

function RatingReveal({
  myVerdict, count, yesPercent, onRevote, onMakeOwn, signedIn,
}: {
  myVerdict: 'yes' | 'no';
  count: number | null;
  yesPercent: number | null;
  onRevote: () => void;
  onMakeOwn: () => void;
  signedIn: boolean;
}) {
  const pct = yesPercent ?? 0;
  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <Eyebrow tone="coral" letter={2}>Thanks for rating</Eyebrow>
        <div
          className="serif"
          style={{
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: -0.3,
            marginTop: 8,
            fontStyle: 'italic',
          }}
        >
          Here's how everyone else feels.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 0' }}>
        <RingChart pct={pct} size={160} label="Would date" />
      </div>

      <div
        style={{
          marginTop: 18,
          padding: 14,
          background: 'var(--surface-2)',
          borderRadius: 14,
          fontSize: 12,
          color: 'var(--ink-3)',
          textAlign: 'center',
        }}
      >
        {count != null
          ? <>{count} rating{count === 1 ? '' : 's'} · you're {pct >= 50 ? 'with the majority' : 'in the minority'}</>
          : 'You just gave the first rating'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
        <span
          style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 100,
            background: myVerdict === 'yes' ? 'var(--coral)' : 'var(--ink)',
            color: '#fff', fontSize: 12, fontWeight: 600,
          }}
        >
          {myVerdict === 'yes' ? '✓ You said yes' : '✗ You said no'}
        </span>
        <button
          type="button"
          onClick={onRevote}
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--ink-3)', fontSize: 12,
            textDecoration: 'underline', cursor: 'pointer', padding: 0,
            fontFamily: 'var(--font-ui)',
          }}
        >
          change my mind
        </button>
      </div>

      {/* "Build my card" CTA only renders for anonymous raters — it's
          the signup driver. Logged-in raters already have a card; no
          need to push them at one. */}
      {!signedIn && (
        <div style={{ marginTop: 18 }}>
          <Btn variant="primary" size="lg" full onClick={onMakeOwn} icon={<Icons.spark size={16} />}>
            Build my card
          </Btn>
        </div>
      )}
    </>
  );
}

function NoteField({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const nearLimit = value.length > RATING_NOTE_MAX - 30;
  return (
    <div style={{ marginTop: 4 }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, RATING_NOTE_MAX))}
        placeholder="Leave an anonymous note (optional)"
        disabled={disabled}
        rows={3}
        style={{
          width: '100%',
          minHeight: 88,
          resize: 'none',
          background: 'var(--surface-2)',
          border: 'none',
          borderRadius: 14,
          padding: 14,
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          color: 'var(--ink)',
          outline: 'none',
        }}
      />
      <div
        className="mono"
        style={{
          textAlign: 'right',
          fontSize: 11,
          color: nearLimit ? 'var(--coral)' : 'var(--ink-3)',
          marginTop: 4,
        }}
      >
        {value.length}/{RATING_NOTE_MAX}
      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

function setMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function setMetaProperty(property: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('property', property); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function capitalize(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + '…';
}
